import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import StackedLayoutWithSidebar from './component/Sidedrawer';
import routes from './Routes/Route';
import axios from 'axios';
import i18n from './i18n';

// Set the base URL for all Axios requests:
const baseURL = process.env.REACT_APP_URL;
// Always log the base URL (useful for debugging in both dev and production)
console.log('🌐 Frontend API Base URL:', baseURL || 'Not set (using relative URLs)');
if (process.env.NODE_ENV === 'development') {
  console.log('📍 Full API URL will be:', baseURL ? `${baseURL}/[endpoint]` : 'Relative URLs (same origin)');
}
axios.defaults.baseURL = baseURL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

function clearUserSession() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('is_first_login');
  localStorage.removeItem('role');
  localStorage.removeItem('email');
  localStorage.removeItem('isFirstLogin');
  localStorage.removeItem('name');
  localStorage.removeItem('userId');
}

function isVendorApiUrl(url) {
  if (!url) return false;
  return url.includes('/api/vendor/') || url.includes('/api/vendor-auth/');
}

// Add language to all API requests; refresh Bearer token so it is not stuck from initial page load
axios.interceptors.request.use(config => {
  const url = config.url || '';
  if (!isVendorApiUrl(url)) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Get current language from i18n or localStorage
  const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || 'en';
  
  // Add language to headers (backend can read from Accept-Language)
  config.headers['Accept-Language'] = currentLanguage;
  
  // Also add as query parameter for flexibility
  config.params = {
    ...config.params,
    lang: currentLanguage
  };
  
  // Log API requests in development
  if (process.env.NODE_ENV === 'development') {
    const fullUrl = config.baseURL 
      ? `${config.baseURL}${config.url}` 
      : `${window.location.origin}${config.url}`;
    console.log(`📤 API Request: ${config.method?.toUpperCase()} ${fullUrl}`, {
      params: config.params,
      headers: { 'Accept-Language': currentLanguage }
    });
  }
  
  return config;
});

// Log API responses in development
axios.interceptors.response.use(
  response => {
    if (process.env.NODE_ENV === 'development') {
      const fullUrl = response.config.baseURL 
        ? `${response.config.baseURL}${response.config.url}` 
        : `${window.location.origin}${response.config.url}`;
      console.log(`📥 API Response: ${response.config.method?.toUpperCase()} ${fullUrl}`, {
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  error => {
    if (process.env.NODE_ENV === 'development') {
      const fullUrl = error.config?.baseURL 
        ? `${error.config.baseURL}${error.config.url}` 
        : error.config?.url ? `${window.location.origin}${error.config.url}` : 'Unknown';
      console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${fullUrl}`, {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data
      });
    }

    const status = error.response?.status;
    const reqUrl = error.config?.url || '';
    if (status === 401) {
      if (reqUrl.includes('/api/vendor/')) {
        if (localStorage.getItem('vendorAccessToken')) {
          localStorage.removeItem('vendorAccessToken');
          window.location.replace('/vendor/login');
        }
        return Promise.reject(error);
      }
      if (reqUrl.includes('/api/vendor-auth/')) {
        return Promise.reject(error);
      }
      if (localStorage.getItem('accessToken')) {
        clearUserSession();
        window.location.replace('/login');
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

function App() {
  return (
    <BrowserRouter>
      <RouteWrapper />
    </BrowserRouter>
  );
}

function RouteWrapper() {
  const location = useLocation();

  // Define routes where Sidebar should not appear
  const noSidebarRoutes = [
    '/login',
    '/signup',
    '/',
    '/reset-password',
    '/forgot-password',
    '/set-password',
    '/vendor/login',
    '/vendor/portal',
  ];

  return (
    <div
    style={{
      backgroundColor: 'rgba(217, 217, 217, 0.35)', // 35% opacity
      minHeight: '100vh', // Ensure it covers the full height of the viewport
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'hidden',
      width: '100%',
    }}
    >
      {/* Check if the current route is in the no-sidebar list */}
      {!noSidebarRoutes.includes(location.pathname) ? (
        <Routes>
          <Route path="/" element={<StackedLayoutWithSidebar />}>
            {/* Render all dynamic routes inside the layout */}
            {routes.map((route, index) => (
              <Route key={index} path={route.path} element={route.component} />
            ))}
          </Route>
        </Routes>
      ) : (
        // Directly render routes for no-sidebar pages
        <Routes>
          {routes.map((route, index) => (
            <Route key={index} path={route.path} element={route.component} />
          ))}
        </Routes>
      )}
    </div>
  );
}

export default App;
